import type { IntegrationSettings } from '@vy/types';
import { INTEGRATION_PROVIDERS } from '@vy/types';
import { raise, validateNotNullOrUndefined } from '@vy/utils';
import { useMemo } from 'react';
import { useIntegrationSettings } from '../../../../../../../hooks/requests/use-integration-settings';
import type { NetSuiteConfigurationFormValues } from '../NetSuiteConfigurationFormSchema';
import {
  IntegrationsSettings,
  INVOICE_NUMBER_SYNC_STRATEGY,
  INVOICE_PAYMENT_STATUS_SYNC_STRATEGY,
  TAX_EXPORT_STRATEGY,
} from '@vy/integrations-types';

type FetchNetSuiteSettingsResult = {
  integrationId: string;
  configuration: NetSuiteConfigurationFormValues;
};

export function useFetchNetSuiteSettings(): FetchNetSuiteSettingsResult {
  const integrationSettingsResponse = useIntegrationSettings(INTEGRATION_PROVIDERS.netSuite);
  const netSuiteIntegrationSettings = integrationSettingsResponse.data;

  validateNotNullOrUndefined(netSuiteIntegrationSettings, 'netSuiteIntegrationSettings');

  const { integrationId, integrationSettings } = netSuiteIntegrationSettings;

  return useMemo(
    () => ({
      integrationId,
      configuration: mapIntegrationSettingsToFormValues(integrationSettings),
    }),
    [integrationId, integrationSettings],
  );
}

function mapIntegrationSettingsToFormValues(
  integrationSettings: IntegrationSettings | IntegrationsSettings,
): NetSuiteConfigurationFormValues {
  if (integrationSettings.integrationProvider !== INTEGRATION_PROVIDERS.netSuite) {
    raise(`Invalid integration provider ${integrationSettings.integrationProvider}`);
  }

  return {
    defaultReceivableAccount: integrationSettings.integrationConfiguration.defaultReceivableAccount ?? '',
    defaultProduct: integrationSettings.integrationConfiguration.defaultProduct ?? null,
    taxExportStrategy:
      integrationSettings.integrationConfiguration.taxExportStrategy ?? TAX_EXPORT_STRATEGY.INCLUDE_IN_AMOUNT,
    accountTaxItem:
      integrationSettings.integrationConfiguration.accountTaxItem ?? null,
    taxItemBySubsidiaryId:
      integrationSettings.integrationConfiguration.taxItemBySubsidiaryId ?? undefined,
    autoExportInvoices: integrationSettings.integrationConfiguration.autoExportInvoices ?? false,
    autoExportCreditMemos: integrationSettings.integrationConfiguration.autoExportCreditMemos ?? false,
    autoExportCustomers: integrationSettings.integrationConfiguration.autoExportCustomers ?? false,
    invoiceNumberSyncStrategy:
      integrationSettings.integrationConfiguration.invoiceNumberSyncStrategy ??
      INVOICE_NUMBER_SYNC_STRATEGY.KEEP_SEPARATE,
    invoicePaymentStatusSyncStrategy:
      integrationSettings.integrationConfiguration.invoicePaymentStatusSyncStrategy ??
      INVOICE_PAYMENT_STATUS_SYNC_STRATEGY.KEEP_SEPARATE,
    summarizeLineItemsByProductId:
      integrationSettings.integrationConfiguration.summarizeLineItemsByProductId ?? false,
    overrideLineItemQuantity:
      integrationSettings.integrationConfiguration.overrideLineItemQuantity ?? false,
    linkInvoiceToSalesOrder:
      integrationSettings.integrationConfiguration.linkInvoiceToSalesOrder ?? false,
    defaultStripeDepositToAccountId:
      integrationSettings.integrationConfiguration.defaultStripeDepositToAccountId ?? null,
  };
}
